<template>
  <div class="cert-form app-container">
    <!--认证描述文字-->
    <div class="cert-desc flex row-between">
      <div>
        精彩纵横云采购平台
        致力于打造真实、合法、有效的互联网招标采购平台。为了更好的保障你和广大用户的权益，请你认真填写以下登记信息。
      </div>
      <div><span class="font-color-danger">*</span> 为必填项</div>
    </div>

    <div class="card flex-1">
      <div class="cert-identity flex">
        当前身份：{{ currentSelectedService.label }}
        <el-button type="text" @click="toSelectPage">修改身份</el-button>
      </div>

      <div class="section">
        <base-section-title title="选择主体类型" class="section-title m-b-16" />
        <div class="section-content">
          <div class="font-color-second font-12">
            请选择你的主体类型后按照对应的类别进行认证，主体认证成功后，将无法变更主体企业；企业包含：企业、分支机构、私营企业等。
          </div>

          <el-form
            ref="companyType"
            class="form-style m-t-32"
            label-width="150px"
            label-suffix="："
            size="small"
            :model="formData"
            :rules="formRules"
          >
            <el-form-item label="选择主体类型" prop="companyType">
              <div class="flex company-type-btn-group">
                <el-button
                  v-for="item in currentSelectedService.companyType"
                  :key="item.label"
                  size="small"
                  class="flex-1"
                  :class="{ active: formData.companyType === item.value }"
                  @click="changeCompanyType(item)"
                >
                  {{ item.label }}
                </el-button>
              </div>
            </el-form-item>
          </el-form>
        </div>
      </div>

      <enterprise-form
        v-if="formData.companyType === companyTypeDict.ENTERPRISE.value"
        ref="companyInfo"
        :company-info="companyInfo"
        :company-type="formData.companyType"
        :company-name-cache="companyNameCache"
        :social-credit-code-cache="socialCreditCodeCache"
        :service-type="formData.serviceType"
      />
      <public-institution-form
        v-if="formData.companyType === companyTypeDict.PUBLIC_INSTITUTION.value"
        ref="companyInfo"
        :company-info="companyInfo"
        :company-type="formData.companyType"
        :company-name-cache="companyNameCache"
        :social-credit-code-cache="socialCreditCodeCache"
        :service-type="formData.serviceType"
      />
      <self-employed-form
        v-if="formData.companyType === companyTypeDict.SELF_EMPLOYEE.value"
        ref="companyInfo"
        :company-info="companyInfo"
        :company-type="formData.companyType"
        :company-name-cache="companyNameCache"
        :social-credit-code-cache="socialCreditCodeCache"
        :service-type="formData.serviceType"
      />
      <person-form
        v-if="formData.companyType === companyTypeDict.PERSON.value"
        ref="companyInfo"
        :company-info="companyInfo"
        :company-type="formData.companyType"
        :company-name-cache="companyNameCache"
        :social-credit-code-cache="socialCreditCodeCache"
        :service-type="formData.serviceType"
      />
    </div>

    <div class="card flex row-right p-v-12 p-h-24">
      <el-button plain @click="toSelectPage">返回上一页</el-button>
      <el-button type="primary" @click="submit">提交认证</el-button>
    </div>
  </div>
</template>

<script>
// 组件
import BaseSectionTitle from '@/components/BaseSectionTitle'
import EnterpriseForm from '@/views/enterprise-cert/component/EnterpriseForm'
import PublicInstitutionForm from '@/views/enterprise-cert/component/PublicInstitutionForm'
import SelfEmployedForm from '@/views/enterprise-cert/component/SelfEmployedForm'
import PersonForm from '@/views/enterprise-cert/component/PersonForm'

// 工具函数
import { requiredRule } from '@/utils/form-valid'
import { mapState } from 'vuex'
import { cloneDeep } from 'lodash'

// 数据字典
import { companyTypeDict, platformServiceDict } from '@/dictionary/company'

// 后端api接口
import { enterCompanyApi } from '@/api/employee'
import {
  approveCompanyApi,
  getCompanyAuditInfoApi,
  getServiceAuditInfoApi,
  getCompanyConnectApi,
  editApproveCompanyApi,
  getCompanyInfoApi,
  getCompanyBusinessInfoApi
} from '@/api/company'

export default {
  name: 'CertForm',

  components: {
    BaseSectionTitle,
    EnterpriseForm,
    PublicInstitutionForm,
    SelfEmployedForm,
    PersonForm
  },

  beforeRouteEnter(to, form, next) {
    // 当params的service为数组里面的时，才可以进入
    const serviceList = [...platformServiceDict.array.map(value => value.alias)]

    const service = to.params.service
    if (service && !serviceList.includes(service)) {
      // TODO: 后期是否要增加404页面
      return next({ name: 'Login' })
    }

    const isNew = to.params.new
    if (isNew && isNew !== 'new') {
      // TODO: 后期是否要增加404页面
      return next({ name: 'Login' })
    }
    next()
  },

  data() {
    return {
      // 数据字典
      companyTypeDict,

      // 当前用户选择的服务
      service: this.$route.params.service,
      isNew: this.$route.params.new, // 新建企业new为true；审核后的修改，历史、江招未认证数据 这种企业已存在的new为false
      currentSelectedService: {},
      auditCompanyType: '', // 审核的主体类型

      // 编辑时：若公司名称 或 社会信用代码 没有修改 跳过存在校验
      companyNameCache: '',
      socialCreditCodeCache: '',

      // 表单
      formData: {
        serviceType: '',
        companyType: ''
      },

      companyInfo: {},
      companyInfoCache: {},

      formRules: {
        companyType: requiredRule('请选择认证的主体类型')
      }
    }
  },

  computed: {
    ...mapState('user', ['employeeEmail', 'employeeName']),
    ...mapState('company', ['companyId'])
  },

  watch: {
    service: {
      handler: function(val) {
        this.currentSelectedService =
          platformServiceDict.dictArray.find(
            value => value.alias === val || value.value === val
          ) || {}

        this.formData.serviceType = this.currentSelectedService.value || ''
      },
      immediate: true
    },

    'formData.companyType'(val) {
      this.companyNameCache = this.companyInfoCache.companyName
      this.socialCreditCodeCache = this.companyInfoCache.socialCreditCode

      if (this.auditCompanyType === val) {
        this.companyInfo = this.companyInfoCache
      } else {
        this.companyInfo = {}
      }
    }
  },

  created() {
    if (!this.isNew) {
      this.getCompanyAuditInfo()
    }
  },

  methods: {
    /**
     * 前往身份选择页面
     */
    toSelectPage() {
      this.$router.replace({
        name: 'SelectService',
        params: { new: this.isNew }
      })
    },

    /**
     * 切换主体类型
     */
    changeCompanyType(item) {
      // 第一次选择企业类型，不做提示
      if (!this.$refs.companyInfo) {
        this.formData.companyType = item.value
        return
      }

      // 表单内容没有修改不做提示
      if (
        this.$refs.companyInfo.formDataOriginal ===
        JSON.stringify(this.$refs.companyInfo.formData)
      ) {
        this.formData.companyType = item.value
        return
      }

      this.$confirm('更换主体类型会清空当前数据，确定更换吗？', '系统提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(() => {
          this.formData.companyType = item.value
        })
        .catch(() => {})
    },

    /**
     * 查看主体认证的信息
     */
    getCompanyAuditInfo() {
      Promise.all([
        // 多主体后的数据，通过getServiceAuditInfoApi身份，getCompanyAuditInfoApi获取企业的认证信息
        getServiceAuditInfoApi(),
        getCompanyAuditInfoApi(),
        // 留存数据（多主体前注册了企业但是没有认证）（江招数据）
        getCompanyInfoApi(),
        getCompanyBusinessInfoApi(),
        // 获取联系人信息
        getCompanyConnectApi()
      ])
        .then(result => {
          this.service =
            this.service ||
            result[0]?.data?.type ||
            platformServiceDict.array.filter(dict => {
              return (result[2]?.data?.indentityInfo || {})[dict.certification]
            })?.value

          // getCompanyAuditInfoApi
          const companyType =
            result[1]?.data?.companyType || result[2]?.data?.type
          this.auditCompanyType = companyType
          this.formData.companyType = companyType

          const companyInfoData =
            result[1]?.data ||
            Object.assign(
              {},
              {
                companyName: result[2]?.data?.companyName,
                socialCreditCode: result[2]?.data?.socialCreditCode
              },
              result[3]?.data
            ) ||
            {}
          this.companyInfoCache = {
            companyName: companyInfoData.companyName,
            socialCreditCode: companyInfoData.socialCreditCode,
            nature: companyInfoData.nature || '',
            legalPersonInfo: companyInfoData.legalPersonInfo,
            regionInfo: companyInfoData.regionInfo,
            bankInfo: companyInfoData.bankInfo,
            photoInfo: {
              bankLicense: companyInfoData.photoInfo.bankLicense || '',
              businessLicense: companyInfoData.photoInfo.businessLicense || '',
              idCardFront: companyInfoData.photoInfo.idCardFront || '',
              idCardHandHeld: companyInfoData.photoInfo.idCardHandHeld || '',
              idCardReverse: companyInfoData.photoInfo.idCardReverse || '',
              legalPersonPowerOfAttorney:
                companyInfoData.photoInfo.legalPersonPowerOfAttorney || ''
            },

            // getCompanyConnectApi
            contactInfo: result[4]?.data?.contactInfo || {},
            administratorInfo: {
              name: this.employeeName,
              email: this.employeeEmail
            }
          }

          // 判断当前服务下，是否含有当前审核返回的主体类型，如果没有，则不再往下执行
          const isCurrentCompanyType =
            (
              platformServiceDict.translate(this.service)?.companyType || []
            ).findIndex(dict => {
              return dict.value === companyType
            }) >= 0
          if (!isCurrentCompanyType) return

          this.companyInfo = cloneDeep(this.companyInfoCache)
          this.companyNameCache = this.companyInfoCache.companyName
          this.socialCreditCodeCache = this.companyInfoCache.socialCreditCode
        })
        .catch(err => {
          if (this.companyId) {
            this.$message.error(err?.data?.message || '获取主体认证信息失败')
          }
        })
    },

    /**
     * 点击提交认证时，校验表单提交
     * @return {Promise<boolean>}
     */
    async validateFun() {
      try {
        await Promise.all([
          this.$refs.companyType.validate(),
          this.$refs.companyInfo.checkFormData()
        ])

        return true
      } catch (e) {
        this.$message.warning('表单填写有误，请按照提示填写')
        return false
      }
    },

    /**
     * 提交主体认证
     * 里面companyId表示公司已经创建，且登录了
     * @return {Promise<void>}
     */
    async submit() {
      if (!(await this.validateFun())) return

      const requestApi = this.isNew ? approveCompanyApi : editApproveCompanyApi
      const formData = cloneDeep(this.formData)

      requestApi(Object.assign({}, formData, this.$refs.companyInfo.formData))
        .then(res => {
          if (this.companyId) {
            this.enterCompany(res.data)
          } else {
            window.location.href = `${process.env.VUE_APP_LOGIN_JUMP_URL}`
          }
        })
        .catch(err => {
          this.$message.error(err?.data?.message)
        })
    },

    /**
     * 加入刚创建的公司，后跳转到云采购平台
     */
    enterCompany(companyId) {
      enterCompanyApi({ companyId })
        .then(res => {
          window.location.href =
            this.returnUrl || `${process.env.VUE_APP_LOGIN_JUMP_URL}`
        })
        .catch(err => {
          window.location.href =
            this.returnUrl || `${process.env.VUE_APP_LOGIN_JUMP_URL}`
        })
    }
  }
}
</script>

<style scoped lang="scss">
.cert-form {
  // 认证描述文字的样式
  .cert-desc {
    height: 40px;
    color: $text-second-color;
    font-size: 12px;
    padding: 0 24px;
    background-color: $info-fourth-color;
    border-radius: 4px 4px 0 0;
  }

  // 当前认证身份样式
  .cert-identity {
    height: 48px;
    padding-left: 16px;
    margin: 10px 20px 32px;
    font-size: 20px;
    @include text-bold;
    background-color: $primary-fourth-color;

    .el-button {
      margin-left: 40px;
      font-size: 16px;
    }
  }

  // 卡片样式
  .card {
    border-radius: 4px 4px 0 0 !important;

    ::v-deep .form-style {
      width: 600px;
      margin: 0 auto;
    }

    .company-type-btn-group {
      .active {
        border-color: $primary-color;
        background-color: $primary-fourth-color;
      }
    }
  }
}
</style>
